import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Ed, Reveal } from "../components/Editorial";
import { BikeMap } from "../components/booking/BikeMap";
import { SpotPicker, freeSpots } from "../components/booking/SpotPicker";
import { CheckoutPanel } from "../components/booking/CheckoutPanel";
import { ReceiptModal } from "../components/booking/ReceiptModal";
import { fmtTime, fmtRange } from "../lib/time";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_CARRITO = 12;

const fmtDay = (dateStr, lang) => {
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    weekday: d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { weekday: "short" }),
    day: d.getDate(),
    month: d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "short" }),
  };
};

export default function Booking() {
  const { lang, t } = useLang();
  const b = t.booking;
  const [sessions, setSessions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cls, setCls] = useState(null);
  const [bookedBikes, setBookedBikes] = useState([]);
  const [selectedBikes, setSelectedBikes] = useState([]);
  const [spotCount, setSpotCount] = useState(1);
  const [receipt, setReceipt] = useState(null);

  // El carrito: varias clases apuntadas y un solo pago al final. Quien compra un
  // paquete saca la semana entera de una sentada en vez de entrar doce veces.
  const [promo, setPromo] = useState(null);
  const [carrito, setCarrito] = useState([]);
  const [verCarrito, setVerCarrito] = useState(false);
  const [avisoCarrito, setAvisoCarrito] = useState("");

  const isFloor = !!cls && cls.kind === "floor";

  const loadSessions = useCallback(() => {
    // Ventana de reservas: hasta 1 mes por adelantado.
    axios.get(`${API}/classes?days=30`).then((r) => {
      setSessions(r.data);
      if (r.data.length) setSelectedDate((d) => d || r.data[0].date);
    });
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    axios.get(`${API}/promo`).then((r) => setPromo(r.data && r.data.code ? r.data : null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!avisoCarrito) return;
    const id = setTimeout(() => setAvisoCarrito(""), 3500);
    return () => clearTimeout(id);
  }, [avisoCarrito]);

  // Regreso desde Stripe: ?lote=<id> tras pagar, ?lote_cancelado=<id> si se abandonó.
  const [payMsg, setPayMsg] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("lote") || params.get("booking");
    const canceled = params.get("lote_cancelado") || params.get("canceled");
    const esLote = !!(params.get("lote") || params.get("lote_cancelado"));
    const clean = () => window.history.replaceState({}, "", window.location.pathname);

    if (canceled) {
      axios.post(`${API}${esLote ? `/bookings/lote/${canceled}/release` : `/bookings/${canceled}/release`}`).catch(() => {});
      setPayMsg(b.payCanceled);
      clean();
      loadSessions();
      return;
    }
    if (!paid) return;

    // El webhook de Stripe puede tardar un instante: se consulta hasta confirmar.
    let tries = 0;
    let alive = true;
    setPayMsg(b.payChecking);
    const check = async () => {
      if (!alive) return;
      try {
        const r = await axios.get(`${API}${esLote ? `/bookings/lote/${paid}` : `/bookings/${paid}`}`);
        if (r.data.status === "confirmed") {
          setPayMsg("");
          setReceipt(esLote ? r.data : { ...r.data, bookings: [r.data] });
          setCarrito([]);
          clean();
          loadSessions();
          return;
        }
      } catch {
        /* se reintenta */
      }
      if (++tries < 12) setTimeout(check, 2000);
      else {
        setPayMsg(b.payChecking);
        clean();
      }
    };
    check();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dates = useMemo(() => [...new Set(sessions.map((s) => s.date))], [sessions]);
  const dayClasses = useMemo(() => sessions.filter((s) => s.date === selectedDate), [sessions, selectedDate]);

  const isPast = (c) => new Date(`${c.date}T${c.time}:00`) < new Date();
  const enCarrito = useCallback((id) => carrito.some((x) => x.cls.id === id), [carrito]);

  const selectClass = async (c) => {
    const res = await axios.get(`${API}/classes/${c.id}`);
    const d = res.data;
    setCls(d);
    setBookedBikes(d.booked_bikes);
    setSpotCount(1);
    // En las clases de suelo el lugar no se elige: se toma el primero libre.
    setSelectedBikes(d.kind === "floor" ? freeSpots(d.total_bikes, d.booked_bikes, 1) : []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pickSpots = (nextCount) => {
    setSpotCount(nextCount);
    setSelectedBikes(freeSpots(cls.total_bikes, bookedBikes, nextCount));
  };

  const refreshBikes = useCallback(async () => {
    if (!cls) return;
    const res = await axios.get(`${API}/classes/${cls.id}`);
    setBookedBikes(res.data.booked_bikes);
    if (cls.kind === "floor") {
      // Reasigna lugares libres por si alguien reservó mientras tanto.
      const free = cls.total_bikes - res.data.booked_bikes.length;
      const n = Math.max(1, Math.min(spotCount, free));
      setSpotCount(n);
      setSelectedBikes(freeSpots(cls.total_bikes, res.data.booked_bikes, n));
    } else {
      setSelectedBikes((prev) => prev.filter((n) => !res.data.booked_bikes.includes(n)));
    }
  }, [cls, spotCount]);

  const toggleBike = (n) =>
    setSelectedBikes((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, z) => a - z)));

  // Una clase del carrito se quedó sin sitio: se vuelve a pedir el horario para
  // que el cliente vea el estado real antes de intentarlo otra vez.
  const onConflict = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  const anadir = () => {
    if (!selectedBikes.length) {
      setAvisoCarrito(isFloor ? b.errSpots : b.errBikes);
      return;
    }
    if (enCarrito(cls.id)) {
      setAvisoCarrito(b.alreadyInCart);
      return;
    }
    if (carrito.length >= MAX_CARRITO) {
      setAvisoCarrito(b.cartLimit.replace("{n}", MAX_CARRITO));
      return;
    }
    setCarrito((prev) => [...prev, { cls, bikes: selectedBikes }]);
    setAvisoCarrito(b.addedToCart);
    setCls(null);
    setSelectedBikes([]);
    setSpotCount(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const quitar = (id) => setCarrito((prev) => prev.filter((x) => x.cls.id !== id));

  const irAPagar = () => {
    setVerCarrito(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setReceipt(null);
    setCls(null);
    setCarrito([]);
    setVerCarrito(false);
    setSelectedBikes([]);
    setSpotCount(1);
    loadSessions();
    window.scrollTo({ top: 0 });
  };

  const sitiosCarrito = carrito.reduce((n, x) => n + x.bikes.length, 0);
  // Un carrito solo de spinning habla de bicis; si mezcla disciplinas, de lugares.
  const soloBicis = carrito.length > 0 && carrito.every((x) => x.cls.kind !== "floor");
  const palabraSitios =
    sitiosCarrito === 1
      ? soloBicis ? b.bikeWord : b.seatWord
      : soloBicis ? b.bikesWord : b.seatsWord;
  const totalOrientativo = carrito.reduce((n, x) => n + x.bikes.length * x.cls.price, 0);

  // ---------- carrito ----------
  if (verCarrito) {
    return (
      <main className="pt-28 pb-24 px-6 md:px-16 min-h-screen" data-testid="cart-page">
        <Reveal>
          <button
            data-testid="cart-back-btn"
            onClick={() => setVerCarrito(false)}
            className="text-[0.7rem] uppercase tracking-[0.25em] text-zinc-500 hover:text-white transition-colors duration-300"
          >
            {b.addMore}
          </button>
          <h1 className="mt-8 font-serif text-4xl md:text-6xl text-white">
            <Ed text={b.cartHeading} />
          </h1>
          <p className="mt-4 max-w-xl text-sm text-zinc-500 font-light leading-relaxed">{b.cartIntro}</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-3" data-testid="cart-list">
            {carrito.length === 0 ? (
              <p className="border border-dashed border-[#232323] px-6 py-10 text-center font-serif italic text-xl text-zinc-500">
                {b.cartEmpty}
              </p>
            ) : (
              <div className="divide-y divide-white/5 border-y border-white/5">
                {[...carrito]
                  .sort((x, y) => (x.cls.date + x.cls.time).localeCompare(y.cls.date + y.cls.time))
                  .map(({ cls: c, bikes }) => (
                    <div key={c.id} className="py-6 flex items-start justify-between gap-6" data-testid={`cart-item-${c.id}`}>
                      <div className="min-w-0">
                        <p className="font-serif italic text-lg md:text-xl text-[#C5A059]">{c.name[lang]}</p>
                        <p className="mt-1.5 text-sm text-zinc-500 font-light">
                          {c.date} · {fmtRange(c.time, c.time_end)} · {b.with} {c.instructor}
                        </p>
                        <p className="mt-1 text-[0.65rem] uppercase tracking-[0.2em] text-zinc-600">
                          {c.kind === "floor"
                            ? `${bikes.length} ${bikes.length === 1 ? b.spotLabel : b.spotsLabel}`
                            : `${bikes.length === 1 ? b.bike : b.bikes} ${bikes.join(", ")}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-5 shrink-0">
                        <span className="text-sm text-zinc-300">${(bikes.length * c.price).toFixed(2)}</span>
                        <button
                          onClick={() => quitar(c.id)}
                          aria-label={b.remove}
                          data-testid={`cart-remove-${c.id}`}
                          className="text-zinc-600 hover:text-red-400 transition-colors duration-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-2">
            <CheckoutPanel items={carrito} onSuccess={setReceipt} onConflict={onConflict} />
          </div>
        </div>

        {receipt && <ReceiptModal receipt={receipt} onAgain={reset} />}
      </main>
    );
  }

  // ---------- elegir clase y sitio ----------
  return (
    <main className="pt-28 pb-32 px-6 md:px-16 min-h-screen" data-testid="booking-page">
      {payMsg && (
        <p data-testid="payment-message" className="mb-8 border border-[#C5A059]/40 bg-[#C5A059]/10 px-5 py-3 text-center text-sm text-[#C5A059]">
          {payMsg}
        </p>
      )}
      {avisoCarrito && (
        <p data-testid="cart-notice" className="mb-8 border border-[#C5A059]/40 bg-[#C5A059]/10 px-5 py-3 text-center text-sm text-[#C5A059]">
          {avisoCarrito}
        </p>
      )}

      {!cls ? (
        <>
          <Reveal>
            <p className="overline-gold font-sans">
              <Ed text={b.overline} goldItalic={false} />
            </p>
            <h1 className="mt-6 font-serif text-4xl md:text-6xl text-white">
              <Ed text={b.chooseClass} />
            </h1>
            <p className="mt-4 max-w-xl text-sm text-zinc-500 font-light leading-relaxed">{b.cartIntro}</p>
            {promo && (
              <p
                data-testid="promo-line"
                className="mt-5 inline-flex flex-wrap items-center gap-x-3 gap-y-1 border border-[#C5A059]/40 bg-[#C5A059]/[0.07] px-5 py-3 text-sm text-[#C5A059]"
              >
                <span>🎉 {b.promoLine.replace("{pct}", promo.discount_pct)}</span>
                <span className="font-serif text-lg tracking-[0.12em]">{promo.code}</span>
              </p>
            )}
          </Reveal>

          <div className="mt-14 flex gap-3 overflow-x-auto pb-2" data-testid="date-strip">
            {dates.map((d) => {
              const f = fmtDay(d, lang);
              const active = d === selectedDate;
              return (
                <button
                  key={d}
                  data-testid={`date-${d}`}
                  onClick={() => setSelectedDate(d)}
                  className={`shrink-0 px-6 py-4 border transition-colors duration-300 text-center ${
                    active ? "border-[#C5A059] bg-[#C5A059]/10" : "border-[#232323] hover:border-zinc-500"
                  }`}
                >
                  <span className={`block text-[0.6rem] uppercase tracking-[0.25em] ${active ? "text-[#C5A059]" : "text-zinc-500"}`}>
                    {f.weekday}
                  </span>
                  <span className={`block mt-1 font-serif text-2xl ${active ? "text-white" : "text-zinc-400"}`}>{f.day}</span>
                  <span className={`block text-[0.6rem] uppercase tracking-[0.2em] ${active ? "text-[#C5A059]" : "text-zinc-600"}`}>
                    {f.month}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-10 divide-y divide-white/5" data-testid="class-list">
            {dayClasses.map((c, i) => {
              const spotsLeft = c.total_bikes - c.booked_count;
              const soldOut = spotsLeft <= 0;
              const past = isPast(c);
              const puesta = enCarrito(c.id);
              const disabled = soldOut || past || puesta;
              return (
                <motion.button
                  key={c.id}
                  data-testid={`class-${c.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  onClick={() => !disabled && selectClass(c)}
                  disabled={disabled}
                  className={`w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-6 py-7 text-left group ${
                    disabled ? "opacity-35 cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <div className="flex items-baseline gap-5 md:gap-12 min-w-0">
                    <span className="w-24 md:w-32 shrink-0 leading-tight">
                      <span className="block font-serif text-2xl md:text-4xl text-white">{fmtTime(c.time)}</span>
                      {c.time_end && (
                        <span className="block mt-0.5 text-xs md:text-sm text-zinc-500 font-light">– {fmtTime(c.time_end)}</span>
                      )}
                    </span>
                    <div>
                      <p className="font-serif italic text-lg md:text-2xl text-[#C5A059]">{c.name[lang]}</p>
                      <p className="mt-1 text-xs md:text-sm text-zinc-500 font-light">
                        {b.with} {c.instructor} · {c.duration_min} {b.min} · ${c.price}
                      </p>
                    </div>
                  </div>
                  <div className="pl-[6.25rem] sm:pl-0 text-left sm:text-right shrink-0 leading-tight">
                    {puesta ? (
                      <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[#C5A059]">{b.inCart}</span>
                    ) : past ? (
                      <span className="text-[0.65rem] uppercase tracking-[0.25em] text-zinc-600">{b.past}</span>
                    ) : soldOut ? (
                      <span className="text-[0.65rem] uppercase tracking-[0.25em] text-red-500/70">{b.soldOut}</span>
                    ) : (
                      <>
                        <span className="text-[0.65rem] uppercase tracking-[0.25em] text-zinc-500">
                          {spotsLeft} {c.kind === "floor" ? b.spotsLeft : b.spots}
                        </span>
                        <span className="block mt-1 text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          →
                        </span>
                      </>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <Reveal>
            <button
              data-testid="back-to-schedule-btn"
              onClick={() => setCls(null)}
              className="text-[0.7rem] uppercase tracking-[0.25em] text-zinc-500 hover:text-white transition-colors duration-300"
            >
              {b.back}
            </button>
            <h1 className="mt-8 font-serif text-4xl md:text-6xl text-white">
              <Ed text={isFloor ? b.chooseSpots : b.chooseBike} />
            </h1>
            <p className="mt-4 font-serif italic text-xl text-[#C5A059]">
              {cls.name[lang]} · {cls.date} · {fmtRange(cls.time, cls.time_end)} · {b.with} {cls.instructor}
            </p>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              {isFloor ? (
                <SpotPicker capacity={cls.total_bikes} bookedSpots={bookedBikes} count={spotCount} onChange={pickSpots} t={b} />
              ) : (
                <BikeMap bookedBikes={bookedBikes} selected={selectedBikes} onToggle={toggleBike} t={b} />
              )}
              <button
                onClick={refreshBikes}
                className="mt-6 text-[0.65rem] uppercase tracking-[0.25em] text-zinc-600 hover:text-[#C5A059] transition-colors duration-300"
              >
                {b.refreshMap}
              </button>
            </div>

            <div className="lg:col-span-2 bg-[#0C0C0C] border border-[#232323] p-8">
              <p className="overline-gold">{b.summary}</p>
              <div className="mt-6 pb-6 border-b border-white/10">
                <p className="font-serif text-xl text-white">{cls.name[lang]}</p>
                <p className="mt-1 text-sm text-zinc-500 font-light">
                  {cls.date} · {fmtRange(cls.time, cls.time_end)}
                </p>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="selected-bikes-list">
                  {selectedBikes.length === 0 ? (
                    <p className="text-xs text-zinc-600 italic">{isFloor ? b.spotsHint : b.mapHint}</p>
                  ) : (
                    selectedBikes.map((n) => (
                      <span key={n} className="text-xs border border-[#C5A059]/50 text-[#C5A059] px-3 py-1 tracking-widest">
                        {isFloor ? b.spotLabel : b.bike} {n}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-between text-sm text-zinc-400">
                <span>
                  {selectedBikes.length} × ${cls.price} {isFloor ? b.perSpot : b.perBike}
                </span>
                <span className="text-white">${(selectedBikes.length * cls.price).toFixed(2)}</span>
              </div>

              {promo && (
                <p className="mt-4 text-[0.7rem] text-[#C5A059] leading-relaxed">
                  {b.promoAtCheckout.replace("{pct}", promo.discount_pct).replace("{code}", promo.code)}
                </p>
              )}

              <button data-testid="add-to-cart-btn" onClick={anadir} className="btn-gold w-full mt-8 !py-4 text-center">
                {b.addToCart}
              </button>
              {carrito.length > 0 && (
                <button
                  data-testid="go-to-cart-btn"
                  onClick={irAPagar}
                  className="w-full mt-3 py-3 text-[0.65rem] uppercase tracking-[0.3em] text-zinc-400 hover:text-[#C5A059] transition-colors duration-300"
                >
                  {b.goToCart} ({carrito.length})
                </button>
              )}
              <p className="mt-5 text-[0.7rem] text-zinc-600 leading-relaxed">{b.addToCartHint}</p>
            </div>
          </div>
        </>
      )}

      {/* Barra del carrito: siempre a la vista mientras haya clases dentro. */}
      {carrito.length > 0 && !verCarrito && (
        <motion.div
          initial={{ y: 80 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[70] border-t border-[#C5A059]/40 bg-[#0C0C0C]/95 backdrop-blur-md px-6 md:px-16 py-4"
          data-testid="cart-bar"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              <ShoppingBag size={16} className="shrink-0 text-[#C5A059]" />
              <p className="text-sm text-white truncate">
                {carrito.length} {carrito.length === 1 ? b.classWord : b.classesWord}
                <span className="hidden sm:inline text-zinc-500">
                  {" · "}
                  {sitiosCarrito} {palabraSitios}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <span className="font-serif text-xl text-[#C5A059]">${totalOrientativo.toFixed(2)}</span>
              <button onClick={irAPagar} data-testid="cart-bar-btn" className="btn-gold !px-7 !py-3 !text-[0.65rem]">
                {b.goToCart}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {receipt && <ReceiptModal receipt={receipt} onAgain={reset} />}
    </main>
  );
}
